import React from 'react';
import { createUseStyles } from 'react-jss';
import { flexRender, Table as TanstackTable } from '@tanstack/react-table';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import classNames from 'classnames';

import { flex, touchTarget } from '@resources/mixins';
import { getColumnMeta } from '../tableMeta';
import { ThemeProps } from '@styles/theme';
import { messages } from '@shared/components/components.messages';

type Props<T> = {
    instance: TanstackTable<T>,
    isLoading?: boolean,
};

const useStyles = createUseStyles((theme: ThemeProps) => ({
    sortedDesc: {},
    toolbar: {
        ...flex('row', 'flex-start', 'center'),
        gap: theme.spacing(2),
        width: '100%',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        backgroundColor: theme.colors.surface_elevated,
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius * 2,
        flexShrink: 0,
    },
    selectionHeader: {
        ...flex('row', 'center', 'center'),
        ...touchTarget(),
        flexShrink: 0,
    },
    select: {
        ...touchTarget(),
        flex: 1,
        minWidth: 0,
        padding: `0 ${theme.spacing(2)}px`,
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius,
        backgroundColor: theme.colors.surface_elevated,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        fontSize: 15,
    },
    directionButton: {
        ...flex('row', 'center', 'center'),
        ...touchTarget(),
        flexShrink: 0,
        border: `1px solid ${theme.colors.grey_light}`,
        background: 'none',
        borderRadius: theme.radius,
        cursor: 'pointer',
        color: theme.colors.violet_primary,

        '&:disabled': {
            opacity: .3,
            cursor: 'default',
        },
    },
    directionIcon: {
        transform: 'scale(1, 1)',
        transition: 'transform .2s ease',

        '&$sortedDesc': {
            transform: 'scale(1, -1)',
        },
    },
    loader: {
        color: theme.colors.violet_primary,
        flexShrink: 0,
    },
}));

export function TableSortSelect<T extends object = {}>({ instance, isLoading }: Props<T>) {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const sortableColumns = instance
        .getAllLeafColumns()
        .filter(column => column.getCanSort());

    // Header-level cells of selection columns (e.g. a select-all checkbox)
    // would be lost with the header row in card mode — surface them here.
    const selectionHeaders = instance
        .getHeaderGroups()
        .flatMap(group => group.headers)
        .filter(header => getColumnMeta(header.column).cardRole === 'selection');

    const { sorting } = instance.getState();
    const activeSort = sorting[0];

    if (!sortableColumns.length && !selectionHeaders.length && !isLoading) {
        return null;
    }

    const onSortColumnChange = (columnId: string) => {
        if (!columnId) {
            instance.setSorting([]);

            return;
        }
        instance.setSorting([{ id: columnId, desc: activeSort?.desc ?? false }]);
    };

    const onDirectionToggle = () => {
        if (activeSort) {
            instance.setSorting([{ id: activeSort.id, desc: !activeSort.desc }]);
        }
    };

    const columnLabel = (columnId: string) => {
        const column = instance.getColumn(columnId);
        const header = column?.columnDef.header;

        return typeof header === 'string' && header.length ? header : columnId;
    };

    return (
        <div className={styles.toolbar}>
            {selectionHeaders.map(header => (
                <span key={header.id} className={styles.selectionHeader}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                </span>
            ))}

            {sortableColumns.length > 0 && (
                <>
                    <select
                        className={styles.select}
                        value={activeSort?.id ?? ''}
                        aria-label={messages['tableSort.label']}
                        onChange={event => onSortColumnChange(event.target.value)}
                    >
                        <option value="">{messages['tableSort.none']}</option>
                        {sortableColumns.map(column => (
                            <option key={column.id} value={column.id}>
                                {columnLabel(column.id)}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        className={styles.directionButton}
                        onClick={onDirectionToggle}
                        disabled={!activeSort}
                        aria-label={messages['tableSort.toggleDirection']}
                        title={messages['tableSort.toggleDirection']}
                    >
                        <ArrowUpwardOutlinedIcon
                            className={classNames(styles.directionIcon, {
                                [styles.sortedDesc]: activeSort?.desc,
                            })}
                        />
                    </button>
                </>
            )}

            {isLoading && (
                <CircularProgress className={styles.loader} color="inherit" size={24} />
            )}
        </div>
    );
}
